import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import Pagination from '../../components/Pagination';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import { startImpersonation, getAdminToken } from '../../utils/auth';
import { ChevronDown, ChevronRight, MoreVertical, Calendar, X, ChevronUp } from 'lucide-react';

const AdminUsersContent = () => {
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [blockedFilter, setBlockedFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('isBlocked') === 'true' ? 'blocked' : 'all';
  });
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    if (blockedFilter !== 'all' || dateFilter !== 'all' || sort !== 'a-z' || limit !== 20) {
      setCurrentPage(1);
    }
  }, [blockedFilter, dateFilter, sort, limit]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, blockedFilter, dateFilter, customStartDate, customEndDate, sort, limit]);

  const getDateRange = () => {
    if (dateFilter === 'all') {
      return {};
    }

    const now = new Date();
    let startDate = null;
    let endDate = null;

    switch (dateFilter) {
      case 'today':
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(yesterday);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastWeek':
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        startDate = new Date(lastWeek);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        const lastMonth = new Date(now);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        startDate = new Date(lastMonth);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'custom':
        if (customStartDate) {
          startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
        }
        if (customEndDate) {
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        return {};
    }

    const params = {};
    if (startDate) {
      params.startDate = startDate.toISOString();
    }
    if (endDate) {
      params.endDate = endDate.toISOString();
    }
    return params;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const dateParams = getDateRange();
      const params = {
        page: currentPage,
        limit,
        sort: sort === 'z-a' ? 'z-a' : 'a-z',
        ...(searchTerm && { search: searchTerm }),
        ...(blockedFilter !== 'all' && { isBlocked: blockedFilter === 'blocked' }),
        ...dateParams
      };
      const response = await api.get('/admin/users', { params });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      showError(error.response?.data?.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockToggle = async (userId, currentStatus) => {
    const action = currentStatus ? 'unblock' : 'block';
    showConfirm(
      `Are you sure you want to ${action} this user?`,
      async () => {
        try {
          await api.patch(`/admin/users/${userId}/block`, { isBlocked: !currentStatus });
          fetchUsers();
          showSuccess(`User ${action}ed successfully!`);
        } catch (error) {
          showError(error.response?.data?.message || `Error ${action}ing user`);
        }
      }
    );
  };

  const handleDelete = async (userId) => {
    showConfirm(
      'Are you sure you want to delete this user? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/admin/users/${userId}`);
          fetchUsers();
          showSuccess('User deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting user');
        }
      }
    );
  };

  const handleImpersonate = async (userId) => {
    showConfirm(
      'Are you sure you want to login as this user? You will be logged in as the user and can return to admin panel later.',
      async () => {
        try {
          const adminToken = getAdminToken();
          if (!adminToken) {
            showError('Admin session expired. Please login again.');
            return;
          }

          const response = await api.post(`/admin/users/${userId}/impersonate`);
          
          // Start impersonation
          startImpersonation(adminToken, response.data.token, response.data.user);
          
          showSuccess(`Logged in as ${response.data.user.name}`);
          
          // Redirect to home page as the user
          setTimeout(() => {
            navigate('/');
            window.location.reload(); // Reload to update auth state
          }, 1000);
        } catch (error) {
          showError(error.response?.data?.message || 'Error impersonating user');
        }
      }
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  return (
    <>
      <SEO title="Manage Users - Admin" description="Manage users." noindex />

        <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="container-custom py-6 md:py-12">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Users</h1>
              </div>

              {/* Search and Filter */}
              <div className="mb-4 flex flex-col sm:flex-row gap-4">
                <form onSubmit={handleSearch} className="flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                    className="input-field"
                  />
                </form>
                <select
                  value={blockedFilter}
                  onChange={(e) => {
                    setBlockedFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="input-field sm:w-auto"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Users</option>
                  <option value="blocked">Blocked Users</option>
                </select>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sort:</label>
                  <select value={sort} onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                    <option value="a-z">A to Z</option>
                    <option value="z-a">Z to A</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Entries per page:</label>
                  <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 text-sm">
                    {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    showDateFilter || dateFilter !== 'all'
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Date Filter</span>
                  {showDateFilter ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  {dateFilter !== 'all' && (
                    <span className="bg-white/20 dark:bg-gray-900/30 px-1.5 py-0.5 rounded text-xs font-semibold">
                      1
                    </span>
                  )}
                </button>
              </div>

              {/* Date Filter Panel */}
              {showDateFilter && (
                <div className="card p-4 md:p-6 mb-4 animate-slide-down">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Date Filter
                    </h3>
                    <button
                      onClick={() => setShowDateFilter(false)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Close date filter"
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button
                        onClick={() => {
                          setDateFilter('today');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'today'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('yesterday');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'yesterday'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Yesterday
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('lastWeek');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'lastWeek'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Last Week
                      </button>
                      <button
                        onClick={() => {
                          setDateFilter('lastMonth');
                          setCustomStartDate('');
                          setCustomEndDate('');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          dateFilter === 'lastMonth'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Last Month
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={() => {
                          setDateFilter('custom');
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors mb-3 ${
                          dateFilter === 'custom'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        Custom Range
                      </button>
                      {dateFilter === 'custom' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                              className="input-field text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              End Date
                            </label>
                            <input
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                              className="input-field text-sm w-full"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {dateFilter !== 'all' && (
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => {
                            setDateFilter('all');
                            setCustomStartDate('');
                            setCustomEndDate('');
                          }}
                          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-2"
                        >
                          <X className="w-4 h-4" />
                          Clear Date Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

        {loading ? (
          <PageLoader message="Loading users..." />
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sr. No.</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phone</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Joined</th>
                      <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {users.map((user, index) => {
                      const srNo = (currentPage - 1) * limit + index + 1;
                      const isExpanded = expandedRows.has(user._id);
                      const toggleExpand = () => {
                        setExpandedRows(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(user._id)) {
                            newSet.delete(user._id);
                          } else {
                            newSet.add(user._id);
                          }
                          return newSet;
                        });
                      };

                      return (
                        <React.Fragment key={user._id}>
                          <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 md:px-6 py-4 text-gray-600 dark:text-gray-400">{srNo}</td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="font-semibold text-gray-900 dark:text-gray-50">{user.name}</div>
                            </td>
                            <td className="px-4 md:px-6 py-4 text-gray-700 dark:text-gray-300">{user.email}</td>
                            <td className="px-4 md:px-6 py-4 text-gray-700 dark:text-gray-300">{user.phone || 'N/A'}</td>
                            <td className="px-4 md:px-6 py-4">
                              <span
                                className={`badge text-xs ${
                                  user.isBlocked
                                    ? 'badge-error'
                                    : 'badge-success'
                                }`}
                              >
                                {user.isBlocked ? 'Blocked' : 'Active'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 text-gray-700 dark:text-gray-300 text-sm">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <button
                                onClick={toggleExpand}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                                aria-label={isExpanded ? 'Collapse actions' : 'Expand actions'}
                              >
                                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                )}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50 dark:bg-gray-900/50">
                              <td colSpan="7" className="px-4 md:px-6 py-4">
                                <div className="flex flex-wrap gap-3">
                                  <button
                                    onClick={() => {
                                      handleImpersonate(user._id);
                                      setExpandedRows(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(user._id);
                                        return newSet;
                                      });
                                    }}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                                    title="Login as this user"
                                  >
                                    Login as User
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleBlockToggle(user._id, user.isBlocked);
                                      setExpandedRows(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(user._id);
                                        return newSet;
                                      });
                                    }}
                                    className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                                      user.isBlocked
                                        ? 'bg-success-600 text-white hover:bg-success-700'
                                        : 'bg-warning-600 text-white hover:bg-warning-700'
                                    }`}
                                  >
                                    {user.isBlocked ? 'Unblock' : 'Block'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      handleDelete(user._id);
                                      setExpandedRows(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(user._id);
                                        return newSet;
                                      });
                                    }}
                                    className="px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700 transition-colors text-sm font-medium"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {users.map((user, index) => {
                const srNo = (currentPage - 1) * limit + index + 1;
                const isExpanded = expandedRows.has(user._id);
                const toggleExpand = () => {
                  setExpandedRows(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(user._id)) {
                      newSet.delete(user._id);
                    } else {
                      newSet.add(user._id);
                    }
                    return newSet;
                  });
                };

                return (
                  <div key={user._id} className="card p-4 md:p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">#{srNo}</span>
                          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{user.name}</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{user.email}</p>
                        {user.phone && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{user.phone}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span
                            className={`badge text-xs ${
                              user.isBlocked
                                ? 'badge-error'
                                : 'badge-success'
                            }`}
                          >
                            {user.isBlocked ? 'Blocked' : 'Active'}
                          </span>
                          <span className="badge text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            Joined: {new Date(user.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={toggleExpand}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1"
                        aria-label={isExpanded ? 'Collapse actions' : 'Expand actions'}
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        )}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => {
                            handleImpersonate(user._id);
                            setExpandedRows(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(user._id);
                              return newSet;
                            });
                          }}
                          className="btn-outline text-sm py-1.5 px-3 text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400"
                          title="Login as this user"
                        >
                          Login as User
                        </button>
                        <button
                          onClick={() => {
                            handleBlockToggle(user._id, user.isBlocked);
                            setExpandedRows(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(user._id);
                              return newSet;
                            });
                          }}
                          className={`btn-outline text-sm py-1.5 px-3 ${
                            user.isBlocked
                              ? 'text-success-600 dark:text-success-400 border-success-600 dark:border-success-400'
                              : 'text-warning-600 dark:text-warning-400 border-warning-600 dark:border-warning-400'
                          }`}
                        >
                          {user.isBlocked ? 'Unblock' : 'Block'}
                        </button>
                        <button
                          onClick={() => {
                            handleDelete(user._id);
                            setExpandedRows(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(user._id);
                              return newSet;
                            });
                          }}
                          className="btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {users.length === 0 && (
              <div className="card p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-lg">No users found.</p>
              </div>
            )}
          </>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages >= 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              itemsOnCurrentPage={users.length}
              hasNextPage={pagination.currentPage < pagination.totalPages}
              hasPrevPage={pagination.currentPage > 1}
              nextPage={pagination.currentPage + 1}
              prevPage={pagination.currentPage - 1}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        )}
          </div>
        </main>
    </>
  );
};

const AdminUsers = () => {
  return (
    <AdminLayout>
      <AdminUsersContent />
    </AdminLayout>
  );
};

export default AdminUsers;

